import { useSystemHostname } from '#~/agent/hooks.js';
import { useAuth0 } from '#~/auth/index.js';
import { config } from '#~/config/index.js';
import { useDepot, useProducer } from '#~/depot/hooks.js';
import { isElectronApp } from '#~/electron/utils.js';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { AutoLinkController, getAutoLinkController } from './auto-link-controller.js';
import type { AutoLinkContext, AutoLinkError, AutoLinkState } from './auto-link-types.js';
import { DEFAULT_BRIDGE_ONLINE_TIMEOUT_MS, DEFAULT_IPC_TIMEOUT_MS } from './auto-link-types.js';
import {
  AutoLinkBootstrapProvider,
  type AutoLinkBootstrapControl,
  type AutoLinkBootstrapRequest,
  type AutoLinkBootstrapResult,
  type ZeroRoomAutoLinkRunPhase
} from './auto-link-bootstrap-context.js';
import { evaluateAutoLinkVendor } from './auto-link-vendor.js';

interface AutoLinkGuardProps {
  readonly children: ReactElement;
}

// A run and its terminal writes are fenced to the account session that started
// them, never to the Root pass that asked: Root's bootstrap effect aborts its
// pass on every dependency change, which is not a cancellation of the run.
type AutoLinkRunFence = {
  readonly accountId: string;
  readonly generation: number;
};

// One zero-Studio run per account session; later Root passes join it. The run
// settles on the controller's FIRST terminal state, not only when its promise
// settles: abort() publishes `error` at once while that promise may still be
// awaiting a created-Studio cleanup with no deadline, and every joined Root
// pass would hang on it.
type ZeroRoomAutoLinkRun = {
  readonly fence: AutoLinkRunFence;
  readonly settled: Promise<void>;
  isSettled: boolean;
  // What waiters settle with; set together with isSettled.
  terminalState: AutoLinkState | undefined;
  // Set when the controller publishes `verifying_link`, which it does only
  // after linkAgent committed the link. A run that fails after that point
  // still linked the Bridge this session (see ZeroRoomAutoLinkRunPhase).
  linkCommitted: boolean;
};

const isTerminalAutoLinkState = (state: AutoLinkState): boolean =>
  state.status === 'success' ||
  state.status === 'error' ||
  state.status === 'skipped' ||
  state.status === 'inventory_changed';

// Resolves when the run settles or the caller's signal aborts, whichever comes
// first, and never leaves the abort listener attached.
// Per rule: settle-every-terminal-branch (ai.knowledge/global) — every exit settles the waiter.
const waitForRun = (run: Promise<void>, signal: AbortSignal): Promise<'settled' | 'detached'> =>
  new Promise((resolve) => {
    if (signal.aborted) {
      resolve('detached');
      return;
    }
    const detach = (): void => resolve('detached');
    signal.addEventListener('abort', detach, { once: true });
    const settle = (): void => {
      signal.removeEventListener('abort', detach);
      resolve('settled');
    };
    void run.then(settle, settle);
  });

// Synchronous controller→depot mirror with the terminal latch. Lives OUTSIDE
// React on purpose: the success transition triggers the room navigation,
// which can unmount the guard before a [state]-keyed effect commits the
// terminal value — stranding the depot at 'checking' and holding the
// BootOverlay (which reads only the depot) visible+opaque forever (win run
// 27327489117: BootOverlay logged autoLink:checking 126ms AFTER the guard
// observed success→idle; the e2e probes then read autolink-checking for the
// remaining 95s). useProducer() is a plain accessor (depot.getState().produce),
// safe outside components.
//
// Terminal latch: once this session has reached `success`/`skipped` in the
// depot, do NOT republish a regression back to a non-terminal in-progress
// state — a remounted guard resets the controller singleton and re-runs
// idle → checking, which would otherwise re-raise the BootOverlay. `error`
// is intentionally still allowed through so genuine failures surface.
const mirrorAutoLinkStateToDepot = (state: AutoLinkState): void => {
  useProducer()((draft) => {
    const currentStatus = draft.boot.autoLinkState.status;
    const alreadyLinkedThisSession = currentStatus === 'success' || currentStatus === 'skipped';
    const incomingInProgress =
      state.status === 'idle' ||
      state.status === 'checking' ||
      state.status === 'linking' ||
      state.status === 'verifying_link';
    if (alreadyLinkedThisSession && incomingInProgress) {
      return;
    }
    draft.boot.autoLinkState = state;
  });
};

const NETWORK_BUFFER_MS = 5_000;

// E2E mode extends setup budget for cold Lambda + retry headroom. 120s base
// covers a healthy CI Mac; fault-injection sweeps (AUTH_DELAY_START_MS &
// siblings) extend the budget by the max service-delay env var so a service
// that's intentionally sleeping doesn't blow through the guard. Production
// users never set IRIS_E2E_TEST=1 so they use the longer non-E2E formula.
export const IS_E2E_TEST =
  typeof window !== 'undefined' && window.electron?.process?.env?.IRIS_E2E_TEST === '1';
function readServiceDelayMs(): number {
  const env =
    (typeof window !== 'undefined' && window.electron?.process?.env) ||
    ({} as Record<string, string | undefined>);
  const read = (k: string): number => {
    const v = env[k];
    const n = v ? Number(v) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  return Math.max(
    read('AUTH_DELAY_START_MS'),
    read('ROUTER_DELAY_START_MS'),
    read('ENCODER_DELAY_START_MS'),
    read('CV_SERVICE_DELAY_START_MS')
  );
}
export const MAX_SETUP_TIME_MS = IS_E2E_TEST
  ? 120_000 + readServiceDelayMs()
  : DEFAULT_IPC_TIMEOUT_MS * 2 + NETWORK_BUFFER_MS * 4 + DEFAULT_BRIDGE_ONLINE_TIMEOUT_MS;

// Checking-phase IPC deadline. The controller's `ipcTimeoutMs` governs ONLY
// the `checking`-phase lookups (getLocalBridgeId / getLinkAgentIds); the
// linking and verifying phases use their own bridge-online timeout and retry
// budget. Those checking lookups depend on the bridge -> auth token path,
// which on Windows can hang indefinitely when the auth sidecar logs
// "timeout while waiting for token" — the IPC neither resolves nor rejects.
// On the global 300s ceiling the boot overlay then stays opaque for five
// minutes, far past any boot budget, and the e2e bridge-offline render step
// times out. In E2E mode we bound the checking IPC to a short base plus the
// injected service delay so a wedged token path rejects promptly, the
// controller transitions checking -> error -> skipped, and the overlay
// dismisses within the test budget. Production keeps the generous global
// ceiling so a genuinely slow cold start is never pre-empted.
const CHECKING_PHASE_BASE_MS = 30_000;
export const CHECKING_IPC_TIMEOUT_MS = IS_E2E_TEST
  ? CHECKING_PHASE_BASE_MS + readServiceDelayMs()
  : DEFAULT_IPC_TIMEOUT_MS;
// Success releases children immediately — the 1s hold was pure UX padding
// (see commit message). Error keeps a 5s read-it pause before mounting children.
const ERROR_DELAY_MS = 5_000;
// A cleared stale cross-account link is NOT a failure the user must read: the
// sidecar wipes its credentials and reboots the app into the fresh-install
// link path (observed ~11s after the transition). Releasing at the generic
// 5s hold dropped the overlay — and let the onboarding funnel raise its
// "Unable to link your Bridge" screen — for the seconds before that reboot.
// Hold the benign "Setting up your bridge…" state through the reboot window
// instead. The release below still fires when the reboot never lands
// (suppressed under IRIS_E2E_TEST, or the sidecar's reboot request was lost);
// 30s matches the sidecar's own mailroom unlink ceiling, so a reboot that has
// not landed by then is not coming.
export const STALE_LINK_REBOOT_GRACE_MS = 30_000;

// The error-hold release, detached from React. The guard lives on the index
// route and root bootstrap can navigate away (unmounting it) while the hold is
// still running; a release that dies with the effect would strand the depot
// in `error` and keep the BootOverlay opaque with no exit when the reboot
// never lands. Writes the same release the effect would have, unless a newer
// transition already replaced that error. Armed from two places: the hold
// effect's unmount cleanup (error already on screen) and the unmount marker
// below (controller still in flight — its error arrives after the guard is
// gone, through the construction-time depot mirror, and would otherwise have
// no release at all).
// Per rule: settle-every-terminal-branch (ai.knowledge/global) — every exit of
// the error hold (in-effect, unmount mid-hold, error after unmount) must
// release the depot; do not inline this back into one branch.
// Ownership epoch for detached error-hold releases. A detached release is
// only valid for the guard generation that scheduled it: a retired guard's
// timer must never release a NEWER controller's error hold, even when the
// newer controller fails with the same reason string. Bumped at (1) every new
// guard mount (new controller singleton — the old generation is retired) and
// (2) every new schedule (a newer release supersedes an older one). The
// reason match below stays as a same-generation safety net. Source-review
// finding: product-diff-review P2 (timer not tied to the controller that
// scheduled it).
let detachedReleaseEpoch = 0;
const scheduleDetachedErrorRelease = (delayMs: number, reason: AutoLinkError | undefined): void => {
  // Bump first: anything scheduled before this call belongs to an earlier
  // hold and is superseded.
  const epoch = ++detachedReleaseEpoch;
  setTimeout(() => {
    useProducer()((draft) => {
      const current = draft.boot.autoLinkState;
      if (epoch !== detachedReleaseEpoch) {
        return;
      }
      if (current.status !== 'error' || current.reason !== reason) {
        return;
      }
      draft.boot.autoLinkState = { status: 'skipped', reason: 'attempts_exhausted' };
    });
  }, delayMs);
};

// Arm the detached release for a controller that outlives the guard. Called
// from the binding cleanup on a TRUE unmount, before abort(): the controller
// keeps its construction-time depot mirror, so its eventual terminal (the
// abort's own `error`, or a late stale-link `error`) lands in the depot with no
// hold effect left to release it. Watch for that one transition and hand it to
// the detached release with the same hold the effect would have used.
// Per rule: settle-every-terminal-branch (ai.knowledge/global).
const armDetachedTerminalRelease = (controller: AutoLinkController): void => {
  const current = controller.getState();
  const inFlight =
    current.status === 'idle' ||
    current.status === 'checking' ||
    current.status === 'linking' ||
    current.status === 'verifying_link';
  if (!inFlight) {
    return;
  }
  let unsubscribe: () => void = () => {};
  unsubscribe = controller.subscribe((next) => {
    if (next.status === 'error') {
      scheduleDetachedErrorRelease(
        next.reason === 'stale_link_cleared' ? STALE_LINK_REBOOT_GRACE_MS : ERROR_DELAY_MS,
        next.reason
      );
      unsubscribe();
    } else if (next.status === 'success' || next.status === 'skipped') {
      unsubscribe();
    }
  });
};

/**
 * `<AutoLinkGuard>` is now a STATE-PUBLISHER, not a UI-renderer.
 *
 * Per `stay-in-scope` (ai.knowledge/global) and the gyfky-W1 spec:
 * the guard owns the auto-link state machine (controller, timeouts,
 * ERROR_DELAY_MS hold) but writes every transition into
 * `depot.boot.autoLinkState`. The single `<BootOverlay>` mounted
 * above the protected route subtree reads that slice and gates
 * its visibility — there is no more local `<AutoLinkLoadingScreen>`
 * mount inside the Guard.
 *
 * Per `acknowledge-rules-publicly` (ai.knowledge/global): replacing
 * the inline render with `return children` here is what makes the
 * "single-mount overlay" architecture possible — children mount
 * unconditionally underneath the overlay; the overlay's opacity
 * gate (not a remount) drives visibility.
 */
export function AutoLinkGuard({ children }: AutoLinkGuardProps): ReactElement {
  const { getAccessTokenSilently, user: auth0User, isLoading: authLoading } = useAuth0();
  const hostname = useSystemHostname();
  const isElectron = isElectronApp();
  // Canonical Auto Link gate. The vendor/Electron decision routes through the
  // single fail-closed helper so Hive / unknown / missing / garbage vendor
  // never starts Auto Link. An already-verified link is handled orthogonally
  // by the account-first bootstrap (completeWithoutAutoLink), not here.
  // Per rule: reference-rules-in-comments (ai.knowledge/global) — fail-closed
  // vendor resolution.
  const vendorDecision = evaluateAutoLinkVendor({ isElectron, vendor: config.vendor });
  const shouldBypassAutoLink = !vendorDecision.eligible;
  const accountId = auth0User?.sub;
  const currentAccountRef = useRef(auth0User?.sub);
  const accountGenerationRef = useRef(0);
  const generationAccountRef = useRef(accountId);
  const activeRunFenceRef = useRef<AutoLinkRunFence | undefined>(undefined);
  const errorReleaseFenceRef = useRef<AutoLinkRunFence | undefined>(undefined);
  const currentSubscriptionTokenRef = useRef<symbol | undefined>(undefined);
  const runRef = useRef<ZeroRoomAutoLinkRun | undefined>(undefined);
  // Read at run start only, so a late hostname never changes the identity of
  // requestZeroRoomAutoLink, which Root's bootstrap effect depends on.
  const hostnameRef = useRef(hostname);
  hostnameRef.current = hostname;
  if (generationAccountRef.current !== accountId) {
    generationAccountRef.current = accountId;
    accountGenerationRef.current += 1;
  }
  currentAccountRef.current = auth0User?.sub;

  // Depot producer — every controller transition + bypass / skipped /
  // released-after-error case writes here so the BootOverlay reads a
  // single source of truth.
  const produce = useDepot((µ) => µ.produce, shallow);

  const [state, setState] = useState<AutoLinkState>({ status: 'idle' });
  const [hasStarted, setHasStarted] = useState(false);
  const [releaseSignal, setReleaseSignal] = useState(0);
  // Controller transitions already publish synchronously through their own
  // subscription. Guard-owned transitions (vendor bypass, auth bypass, caught
  // errors, delayed error release) must do the same explicitly; a local-only
  // setState leaves BootOverlay reading stale `idle` forever. Per FC-3.5's
  // persisted/direct-state boundary and code-comments-required.
  const publishGuardState = useCallback((next: AutoLinkState): void => {
    setState(next);
    mirrorAutoLinkStateToDepot(next);
  }, []);
  const isAutoLinkInProgress =
    hasStarted &&
    (state.status === 'idle' ||
      state.status === 'checking' ||
      state.status === 'linking' ||
      state.status === 'verifying_link');

  useEffect(() => {
    runRef.current = undefined;
    activeRunFenceRef.current = undefined;
    errorReleaseFenceRef.current = undefined;
    setHasStarted(false);
    setState({ status: 'idle' });
    // Account-scoped reset intentionally bypasses the same-session terminal latch.
    produce((draft) => {
      draft.boot.autoLinkState = { status: 'idle' };
    });
  }, [auth0User?.sub, produce]);

  // Unmount marker. Declared BEFORE the binding effect below and keyed on []
  // so its cleanup runs only on a TRUE unmount, ahead of the binding cleanup
  // (React runs cleanups in declaration order). An account switch replaces the
  // binding without touching this ref, so the two exits stay distinguishable.
  const unmountingRef = useRef(false);
  useEffect(() => {
    unmountingRef.current = false;
    return () => {
      unmountingRef.current = true;
    };
  }, []);

  const controllerBinding = useMemo(() => {
    // Retire every detached release scheduled by a previous guard generation:
    // this binding replaces the controller singleton (per account), so an old
    // generation's pending timer must not fire against THIS controller's
    // error hold.
    detachedReleaseEpoch++;
    AutoLinkController.resetInstance();
    // Bound the checking-phase IPC deadline (controller `ipcTimeoutMs` only
    // governs the checking-phase bridge/auth lookups). In E2E mode this caps a
    // wedged Windows token path so the overlay dismisses within the test budget
    // instead of holding opaque on the 300s global ceiling.
    const created = getAutoLinkController({ ipcTimeoutMs: CHECKING_IPC_TIMEOUT_MS });
    // Depot mirroring is attached HERE (render-independent), not in an
    // effect — see mirrorAutoLinkStateToDepot for the unmount-race narrative.
    const controllerAccountId = accountId;
    const controllerGeneration = accountGenerationRef.current;
    let lifecycleToken = Symbol('auto-link-subscription');
    let active = true;
    let unsubscribeDepot: (() => void) | undefined;
    currentSubscriptionTokenRef.current = lifecycleToken;

    const isCurrentLifecycle = (token: symbol): boolean => {
      const runFence = activeRunFenceRef.current;
      const runIsCurrent =
        runFence === undefined ||
        (runFence.accountId === controllerAccountId &&
          runFence.generation === controllerGeneration);
      return (
        active &&
        token === lifecycleToken &&
        token === currentSubscriptionTokenRef.current &&
        controllerAccountId === currentAccountRef.current &&
        controllerGeneration === accountGenerationRef.current &&
        runIsCurrent
      );
    };

    const subscribeDepot = (): void => {
      if (unsubscribeDepot !== undefined) return;
      const token = lifecycleToken;
      unsubscribeDepot = created.subscribe((next) => {
        if (isCurrentLifecycle(token)) {
          mirrorAutoLinkStateToDepot(next);
        }
      });
    };

    const activate = (): void => {
      if (!active) {
        lifecycleToken = Symbol('auto-link-subscription');
        active = true;
        currentSubscriptionTokenRef.current = lifecycleToken;
      }
      subscribeDepot();
    };

    const deactivate = (): void => {
      active = false;
      if (currentSubscriptionTokenRef.current === lifecycleToken) {
        currentSubscriptionTokenRef.current = undefined;
      }
      const unsubscribe = unsubscribeDepot;
      unsubscribeDepot = undefined;
      unsubscribe?.();
    };

    const subscribeLocal = (): (() => void) => {
      const token = lifecycleToken;
      return created.subscribe((next) => {
        if (!isCurrentLifecycle(token)) return;
        if (next.status === 'error' && controllerAccountId !== undefined) {
          errorReleaseFenceRef.current = activeRunFenceRef.current ?? {
            accountId: controllerAccountId,
            generation: controllerGeneration
          };
        }
        setState(next);
      });
    };

    subscribeDepot();
    // Mirror the current state once so the depot reflects the controller
    // from construction (parity with the old effect's mount publish).
    if (isCurrentLifecycle(lifecycleToken)) {
      mirrorAutoLinkStateToDepot(created.getState());
    }
    return { activate, controller: created, deactivate, subscribeLocal };
  }, [accountId]);
  const controller = controllerBinding.controller;

  useEffect(() => {
    // React Strict Mode probes cleanup/setup without re-running useMemo. A new
    // lifecycle token makes retained callbacks stale before both subscriptions
    // are re-established. Per `settle-every-terminal-branch`, both unsubscribe.
    controllerBinding.activate();
    const unsubscribeLocal = controllerBinding.subscribeLocal();
    return () => {
      unsubscribeLocal();
      if (unmountingRef.current) {
        // TRUE unmount (ENG-4695): keep the construction-time depot mirror so a
        // terminal written after the guard is gone still reaches the overlay,
        // and arm its detached release BEFORE abort() produces that terminal.
        armDetachedTerminalRelease(controller);
      } else {
        // Account rebinding: this generation is retired; both subscriptions go.
        controllerBinding.deactivate();
      }
      controller.abort();
    };
  }, [controller, controllerBinding]);

  // Depot mirroring moved to the controller subscription installed at
  // construction (mirrorAutoLinkStateToDepot) — a [state]-keyed effect here
  // loses the terminal transition when the success-driven navigation
  // unmounts the guard before the effect commits.

  // NO database pre-warm here: a missing local database.db only predicts Auto
  // Link — it is not authoritative account inventory. The account-first
  // bootstrap (use-root-bootstrap) settles inventory and drives
  // requestZeroRoomAutoLink / completeWithoutAutoLink; publishing a `linking`
  // pre-warm before that settles would show "Setting up your bridge..." to
  // accounts that already have Studios. Covered by the guard's pre-warm tests.
  const requestZeroRoomAutoLink = useCallback(
    async (request: AutoLinkBootstrapRequest): Promise<AutoLinkBootstrapResult> => {
      const requestGeneration = accountGenerationRef.current;
      const isCurrentRequest = (): boolean =>
        !request.signal.aborted &&
        request.accountId === currentAccountRef.current &&
        requestGeneration === accountGenerationRef.current;

      if (
        authLoading ||
        accountId === undefined ||
        request.accountId !== accountId ||
        !isCurrentRequest()
      ) {
        return { kind: 'stale' };
      }

      if (shouldBypassAutoLink) {
        const skipped: AutoLinkState = {
          status: 'skipped',
          reason: !isElectron ? 'not_electron' : 'not_iris_vendor'
        };
        setState(skipped);
        mirrorAutoLinkStateToDepot(skipped);
        return { kind: 'settled', state: skipped };
      }

      let run = runRef.current;
      if (
        run === undefined ||
        run.fence.accountId !== request.accountId ||
        run.fence.generation !== requestGeneration
      ) {
        // Only the guard's own lifecycle cancels this run: an account switch,
        // a true unmount, or the MAX_SETUP_TIME_MS safety timeout.
        const fence: AutoLinkRunFence = {
          accountId: request.accountId,
          generation: requestGeneration
        };
        // A true unmount keeps the account and generation, so it is checked
        // separately: a late failure must not overwrite the terminal that the
        // unmount's abort() already handed to the detached release.
        const isCurrentRun = (): boolean =>
          !unmountingRef.current &&
          fence.accountId === currentAccountRef.current &&
          fence.generation === accountGenerationRef.current;
        activeRunFenceRef.current = fence;
        setHasStarted(true);

        const context: AutoLinkContext = {
          getToken: getAccessTokenSilently,
          userId: auth0User.sub ?? '',
          hostname: hostnameRef.current ?? undefined,
          isElectron,
          vendor: config.vendor
        };
        let resolveSettled: () => void = () => {};
        const started: ZeroRoomAutoLinkRun = {
          fence,
          settled: new Promise<void>((resolve) => {
            resolveSettled = resolve;
          }),
          isSettled: false,
          terminalState: undefined,
          linkCommitted: false
        };
        // Per rule: settle-every-terminal-branch (ai.knowledge/global) — the
        // first terminal transition, a resolve, and a reject all settle here.
        let unsubscribeTerminal: (() => void) | undefined;
        const settleRun = (terminalState: AutoLinkState): void => {
          if (started.isSettled) return;
          started.isSettled = true;
          started.terminalState = terminalState;
          unsubscribeTerminal?.();
          resolveSettled();
        };
        unsubscribeTerminal = controller.subscribe((next) => {
          if (next.status === 'verifying_link') started.linkCommitted = true;
          if (isTerminalAutoLinkState(next)) settleRun(next);
        });
        // subscribe() replays the current state, which may already be terminal.
        if (started.isSettled) unsubscribeTerminal();
        runRef.current = started;
        run = started;

        void controller.startForEmptyInventory(context).then(
          () => settleRun(controller.getState()),
          (error: unknown) => {
            const failed: AutoLinkState = {
              status: 'error',
              reason: 'ipc_failed',
              message: error instanceof Error ? error.message : 'Unknown error'
            };
            // An account switch or a true unmount settles without writing this
            // error into state that no longer belongs to the run.
            if (isCurrentRun()) {
              errorReleaseFenceRef.current = fence;
              setState(failed);
              mirrorAutoLinkStateToDepot(failed);
            }
            settleRun(failed);
          }
        );
      }

      // Every Root pass, including the one that started the run, awaits the
      // same run. A pass whose signal aborts (its effect re-ran) detaches
      // alone and leaves the run to the pass that replaced it.
      const outcome = await waitForRun(run.settled, request.signal);
      if (outcome === 'detached' || !isCurrentRequest()) {
        return { kind: 'stale' };
      }
      return { kind: 'settled', state: run.terminalState ?? controller.getState() };
    },
    [accountId, authLoading, controller, getAccessTokenSilently, isElectron, shouldBypassAutoLink]
  );

  const getZeroRoomAutoLinkRunPhase = useCallback(
    (forAccountId: string): ZeroRoomAutoLinkRunPhase => {
      const run = runRef.current;
      if (
        run === undefined ||
        run.fence.accountId !== forAccountId ||
        run.fence.generation !== accountGenerationRef.current
      ) {
        return 'none';
      }
      if (run.isSettled) {
        if (run.terminalState?.status === 'success') return 'succeeded';
        // A run that ended before committing a link (an error such as
        // cross_account_link, a skip, or an inventory change) leaves the next
        // pass to decide for itself, including the cross-account recovery.
        return run.linkCommitted ? 'link_committed' : 'none';
      }
      // The controller only publishes `linking` once its Studio exists, so a
      // create still in flight reads as `checking`.
      const { status } = controller.getState();
      return status === 'linking' || status === 'verifying_link' ? 'linking' : 'checking';
    },
    [controller]
  );

  const completeWithoutAutoLink = useCallback((request: AutoLinkBootstrapRequest): void => {
    if (request.signal.aborted || request.accountId !== currentAccountRef.current) {
      return;
    }
    const skipped: AutoLinkState = { status: 'skipped', reason: 'account_has_rooms' };
    setState(skipped);
    mirrorAutoLinkStateToDepot(skipped);
  }, []);

  const bootstrapControl = useMemo<AutoLinkBootstrapControl>(
    () => ({ requestZeroRoomAutoLink, completeWithoutAutoLink, getZeroRoomAutoLinkRunPhase }),
    [completeWithoutAutoLink, getZeroRoomAutoLinkRunPhase, requestZeroRoomAutoLink]
  );

  // After ERROR_DELAY_MS, transition the local state from `error` to
  // `skipped`. The overlay (which reads depot.boot.autoLinkState) interprets
  // `skipped` as "auto-link layer is satisfied" and dismisses on that
  // dimension. Per `verify-design-intent-before-removal` (ai.knowledge/global):
  // ERROR_DELAY_MS is preserved — the 5s read-it pause is intentional UX.
  // `releaseSignal` is kept incrementing for compatibility with any external
  // consumer that watches the prop; it has no rendering effect here.
  // Read the reason out of the union so the effect re-keys on it: a
  // self-healing stale-link terminal holds longer than a genuine error.
  const errorReason = state.status === 'error' ? state.reason : undefined;
  useEffect(() => {
    if (state.status !== 'error') {
      return;
    }

    const fence = errorReleaseFenceRef.current;
    const holdMs =
      errorReason === 'stale_link_cleared' ? STALE_LINK_REBOOT_GRACE_MS : ERROR_DELAY_MS;
    const armedAt = Date.now();
    const timer = setTimeout(() => {
      const isCurrentError =
        fence !== undefined &&
        fence.accountId === currentAccountRef.current &&
        fence.generation === accountGenerationRef.current;
      if (!isCurrentError) return;

      const skipped: AutoLinkState = { status: 'skipped', reason: 'attempts_exhausted' };
      setReleaseSignal((value) => value + 1);
      // Per `settle-every-terminal-branch`: the depot terminal mirrors the
      // local release only while its originating run's account session is current.
      publishGuardState(skipped);
    }, holdMs);

    return () => {
      clearTimeout(timer);
      // Unmounted mid-hold: hand the remainder to the detached release so the
      // overlay still gets its exit. A state change (not an unmount) simply
      // re-arms through this effect.
      if (unmountingRef.current) {
        scheduleDetachedErrorRelease(Math.max(0, holdMs - (Date.now() - armedAt)), errorReason);
      }
    };
  }, [state.status, errorReason, publishGuardState]);

  useEffect(() => {
    if (shouldBypassAutoLink && !hasStarted) {
      return;
    }

    if (!isAutoLinkInProgress) return undefined;

    const timeout = setTimeout(() => {
      console.warn('[auto-link-guard] Safety timeout reached');
      controller.abort();
    }, MAX_SETUP_TIME_MS);

    return () => clearTimeout(timeout);
  }, [isAutoLinkInProgress, shouldBypassAutoLink, hasStarted, controller]);

  // Bypass paths — publish a `skipped` state so the overlay knows it
  // can dismiss for the autolink dimension. Per `reference-rules-in-comments`
  // (ai.knowledge/global): the union-of-signals dismiss rule lives in
  // BootOverlay; the guard's job is just to make sure every code path
  // writes a terminal status.
  // Route the observable skip reason through the SAME vendor decision as the
  // bypass gate above so the two never diverge. When the vendor is eligible but
  // the guard still bypasses, use the verified local link as the terminal
  // reason. Calling that path network_unavailable leaves onboarding waiting on
  // a Bridge that is already linked. Per rule: reference-rules-in-comments
  // (ai.knowledge/global).
  const bypassReason = vendorDecision.eligible
    ? ('already_linked_local' as const)
    : vendorDecision.reason;

  useEffect(() => {
    if (shouldBypassAutoLink && state.status === 'idle') {
      publishGuardState({ status: 'skipped', reason: bypassReason });
    }
  }, [shouldBypassAutoLink, state.status, bypassReason, publishGuardState]);

  // Auth-resolved-but-no-user is functionally a skip; same overlay-dismiss
  // semantics as `skipped`. Children mount immediately underneath.
  useEffect(() => {
    if (!authLoading && !auth0User && state.status === 'idle') {
      publishGuardState({ status: 'skipped', reason: 'not_electron' });
    }
  }, [authLoading, auth0User, state.status, publishGuardState]);

  // Holds for ERROR_DELAY_MS so the user can read the error in the overlay
  // before children mount underneath. Read by the overlay via state.
  void releaseSignal;

  // Children ALWAYS render. The overlay (mounted once in router.tsx)
  // sits on top, opacity-gated by the union of:
  //   (1) autoLinkState ∉ {success, skipped}  (with the error-hold carve-out)
  //   (2) !isRootReady
  //   (3) !hasRegistryLoaded
  // Per `stay-in-scope` (ai.knowledge/global): the guard does NOT render
  // a loading screen here — that responsibility moved to BootOverlay.
  return <AutoLinkBootstrapProvider value={bootstrapControl}>{children}</AutoLinkBootstrapProvider>;
}
